import { useQuery, UseQueryOptions } from "@tanstack/react-query";

interface UseDownloadAudioOptions extends Omit<UseQueryOptions<Blob>, "queryKey" | "queryFn" | "enabled"> {
  filename?: string;
  predownload?: boolean;
}

export const useDownloadAudio = (
  src: string,
  { predownload = true, filename, ...queryOptions }: UseDownloadAudioOptions = {}
) => {
  const downloadQuery = useQuery<Blob>({
    queryKey: ["audio-download", src],
    queryFn: async () => {
      const response = await fetch(src);

      if (!response.ok) {
        throw new Error("Download failed");
      }

      // Convert to blob
      return response.blob();
    },
    enabled: predownload && !!src,
    ...queryOptions,
  });

  const getBlobUrl = async () => {
    if (downloadQuery.data) {
      return URL.createObjectURL(downloadQuery.data);
    }
    const blob = await downloadQuery.refetch();
    if (!blob.data) return undefined;
    return URL.createObjectURL(blob.data);
  };

  const download = async () => {
    // Ensure we have the blob URL
    const blobUrl = await getBlobUrl();

    if (!blobUrl) {
      return;
    }

    // Create a temporary link element and trigger download
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename ?? new Date().toISOString();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  };

  return {
    download,
    isLoading: downloadQuery.isLoading,
    localBlobURL: downloadQuery.data,
  };
};
